import { useEffect, useState, type ReactNode } from 'react';

/**
 * Shell visuel commun à tous les drawers d'édition admin.
 *
 * Gère :
 * - Slide-in animation à l'ouverture (mount) et slide-out à la fermeture
 * - Backdrop semi-transparent avec onClick close
 * - ESC handler global
 *
 * Ne gère PAS :
 * - Le contenu du drawer (header, body, footer) : c'est au composant qui
 *   l'utilise de fournir son markup via render prop, ce qui lui permet de
 *   décider de sa propre disposition.
 * - Le state métier (draft, dirty, save) : reste dans la spécialisation.
 *
 * Pattern render prop : on passe un objet { close } au children pour que les
 * boutons internes (X du header, Cancel du footer) puissent déclencher la
 * fermeture animée. Si le parent veut close depuis l'extérieur, il appelle
 * juste onClose lui-même (l'animation se joue quand même au remount).
 *
 * Refacto B3 (2026-05-28) : extrait depuis InvestorEditDrawer et
 * ProgrammeEditDrawer qui dupliquaient ce pattern.
 */
export interface BaseDrawerProps {
  /** Callback final appelé après l'animation de close (démontage parent). */
  onClose: () => void;
  /**
   * Largeur du drawer. Défaut w-full md:w-[540px]. Pour les drawers plus
   * larges (InvestorEditDrawer = 640px), passer la classe explicitement.
   */
  widthClass?: string;
  /**
   * Render prop : reçoit l'API { close } qui déclenche l'animation de
   * fermeture et appelle onClose après 220ms.
   */
  children: (api: { close: () => void }) => ReactNode;
}

export function BaseDrawer({
  onClose,
  widthClass = 'w-full md:w-[540px] md:max-w-[92vw]',
  children,
}: BaseDrawerProps) {
  const [open, setOpen] = useState(false);

  // Slide-in à l'ouverture (déclenchée au prochain tick après mount).
  useEffect(() => {
    const t = setTimeout(() => setOpen(true), 10);
    return () => clearTimeout(t);
  }, []);

  function close() {
    setOpen(false);
    setTimeout(onClose, 220);
  }

  // ESC global pour fermer (pattern standard slide-overs Mac).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  return (
    <>
      <div
        className={
          'fixed inset-0 bg-navy/30 backdrop-blur-[2px] z-30 transition-opacity duration-200 ' +
          (open ? 'opacity-100' : 'opacity-0 pointer-events-none')
        }
        onClick={close}
      />
      <aside
        className={
          'fixed top-0 right-0 z-40 h-full bg-white shadow-drawer flex flex-col transition-transform duration-200 ' +
          widthClass + ' ' +
          (open ? 'translate-x-0' : 'translate-x-full')
        }
      >
        {children({ close })}
      </aside>
    </>
  );
}
